import {
  ApiMasterLabelUPCData,
  ApiMasterLabelCrop,
  MasterUPCData,
  MasterCropData,
  MasterDesignData,
} from '../types';

function optionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function extractBarcodeFromDesignImageUrls(...urls: Array<unknown>): string | undefined {
  for (const url of urls) {
    const text = optionalString(url);
    if (!text) continue;
    const matches = text.match(/\d{8,14}/g);
    if (!matches || matches.length === 0) continue;
    return matches[matches.length - 1];
  }
  return undefined;
}

function transformCrop(api: ApiMasterLabelCrop): MasterCropData {
  return {
    id: api.cropId,
    imageUrl: api.vizUrl,
    isInactive: !api.isActive,
    isPending: api.isPending,
    isMismatch: api.pendingReason === 'unknown_upc',
    storeId: String(api.storeId),
    itemNumber: api.itemNumber || undefined,
    source: api.source || undefined,
    barcode: optionalString(api.barcode),
    normalizedUpc: optionalString(api.normalizedUpc),
    createdAt: api.createdAt || undefined,
    orientation: api.orientation || undefined,
    classificationType: api.classificationType || undefined,
    imageResolution: api.imageResolution || undefined,
    createdBy: api.createdBy || undefined,
    updatedAt: api.updatedAt || undefined,
    updatedBy: api.updatedBy || undefined,
  };
}

export function transformApiUPCData(api: ApiMasterLabelUPCData): MasterUPCData {
  const itemNumber = optionalString(api.itemNumber);
  if (!itemNumber) {
    throw new Error(`Master Label payload missing itemNumber for UPC ${api.upc}`);
  }
  const designs = api.designs
    .map((design): MasterDesignData => {
      const crops = (design.crops ?? []).map(transformCrop);
      const persistedDesignId = optionalString(design._id) || '';
      const representativeCropId = optionalString(design.representativeCropId) || crops[0]?.id || null;
      const frontImageUrl = optionalString(design.frontImageUrl);
      const backImageUrl = optionalString(design.backImageUrl);
      const barcodeImageUrl = optionalString(design.barcodeImageUrl);
      const barcode = optionalString(design.barcode)
        || crops[0]?.barcode
        || extractBarcodeFromDesignImageUrls(barcodeImageUrl, frontImageUrl, backImageUrl);

      return {
        id: persistedDesignId,
        persistedDesignId,
        representativeCropId,
        barcodeStatus: design.barcodeStatus,
        frontImageUrl,
        backImageUrl,
        barcodeImageUrl,
        barcode,
        crops,
      };
    })
    .filter((design) => design.crops.length > 0 || Boolean(design.barcode));

  return {
    upc: api.upc,
    normalizedUpc: api.normalizedUpc,
    productName: api.productName || '',
    productImageUrl: api.productImageUrl || undefined,
    alternativeImageUrls: api.alternativeImageUrls && api.alternativeImageUrls.length > 0
      ? api.alternativeImageUrls
      : api.productImageUrl
        ? [api.productImageUrl]
        : undefined,
    itemNumber,
    barcodeSummary: api.barcodeSummary ? {
      primaryUpcs: api.barcodeSummary.primaryUpcs.map((entry) => ({
        upc: entry.upc,
        provenance: entry.provenance,
        storeDetails: entry.storeDetails.map((detail) => ({
          storeLabel: detail.storeLabel,
          role: detail.role,
          name: detail.name || undefined,
          casePack: detail.casePack || undefined,
          images: detail.images.map((image) => ({
            url: image.url,
            sourceLabel: image.sourceLabel || undefined,
          })),
        })),
      })),
      alternateUpcs: api.barcodeSummary.alternateUpcs.map((entry) => ({
        upc: entry.upc,
        provenance: entry.provenance,
        storeDetails: entry.storeDetails.map((detail) => ({
          storeLabel: detail.storeLabel,
          role: detail.role,
          name: detail.name || undefined,
          casePack: detail.casePack || undefined,
          images: detail.images.map((image) => ({
            url: image.url,
            sourceLabel: image.sourceLabel || undefined,
          })),
        })),
      })),
      casePacks: api.barcodeSummary.casePacks.map((entry) => ({
        upc: entry.upc,
        provenance: entry.provenance,
        storeDetails: entry.storeDetails.map((detail) => ({
          storeLabel: detail.storeLabel,
          role: detail.role,
          name: detail.name || undefined,
          casePack: detail.casePack || undefined,
          images: detail.images.map((image) => ({
            url: image.url,
            sourceLabel: image.sourceLabel || undefined,
          })),
        })),
      })),
    } : undefined,
    snapshotVersion: api.snapshotVersion,
    designs,
    pendingCrops: api.pendingCrops.map(transformCrop),
    unassignedCrops: api.unassignedCrops.map(transformCrop),
    nonFrontCrops: api.nonFrontCrops.map(transformCrop),
    cartonCrops: api.cartonCrops.map(transformCrop),
    inactiveCrops: api.inactiveCrops.map(transformCrop),
  };
}
